import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../api-config';
import { ApiResponse, Service } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

/**
 * Tipo de retorno para paginación server-side
 */
export interface ServicePageResponse {
  items: Service[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Interfaz para crear/actualizar servicio (sin id)
 */
export type CreateServicePayload = Omit<Service, 'id'>;
export type UpdateServicePayload = Partial<Omit<Service, 'id'>>;

@Injectable({ providedIn: 'root' })
export class ServiceService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/services`;
  }

  /**
   * Normaliza el status - mantiene el valor del API tal como viene
   */
  private normalizeStatus(status: string | undefined): Service['status'] {
    if (!status) return 'ACTIVE';
    return status.toUpperCase() as Service['status'];
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend
   */
  private mapToService(item: any): Service {
    return {
      id: item.id || '',
      prefix: item.prefix || '',
      code: item.code || '',
      name: item.name || '',
      description: item.description || '',
      status: this.normalizeStatus(item.status),
      countries: item.countries ?? 0,
      companies: item.companies ?? 0,
      branchs: item.branchs ?? 0,
      agents: item.agents ?? 0,
      dealers: item.dealers ?? 0,
    };
  }

  /**
   * Obtiene todos los servicios (con paginación del backend)
   *
   * Endpoint: GET /api/settings/services?page=&size=&sort=&direction=&status=&search=
   */
  getAll(
    page = 0,
    size = 20,
    sort = 'name',
    direction: 'asc' | 'desc' = 'asc',
    search?: string,
    status?: string,
  ): Observable<ServicePageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim());
    }

    return this.httpClient
      .get<ApiResponse<PageableData>>(this.apiUrl, { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const items = pageableData?.content || [];
          return {
            items: items.map((item: any) => this.mapToService(item)),
            page: pageableData?.page ?? 0,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          };
        }),
      );
  }

  /**
   * Obtiene un servicio por ID
   *
   * Endpoint: GET /api/settings/services/{id}
   */
  getById(id: string): Observable<Service> {
    return this.httpClient
      .get<ApiResponse<any>>(`${this.apiUrl}/${id}`)
      .pipe(map((response) => this.mapToService(response.data)));
  }

  /**
   * Crea un nuevo servicio
   * La API devuelve solo { message: "..." } sin data
   *
   * Endpoint: POST /api/settings/services
   */
  create(data: CreateServicePayload): Observable<{ message: string }> {
    return this.httpClient
      .post<{ success: boolean; message: string }>(this.apiUrl, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Servicio creado exitosamente',
        })),
      );
  }

  /**
   * Actualiza un servicio existente
   * La API devuelve solo { message: "..." } sin data
   *
   * Endpoint: PUT /api/settings/services/{id}
   */
  update(
    id: string,
    data: UpdateServicePayload,
  ): Observable<{ message: string }> {
    return this.httpClient
      .put<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`, data)
      .pipe(
        map((response) => ({
          message: response.message || 'Servicio actualizado exitosamente',
        })),
      );
  }

  /**
   * Elimina un servicio
   * La API devuelve solo { message: "..." } sin data
   *
   * Endpoint: DELETE /api/settings/services/{id}
   */
  delete(id: string): Observable<{ message: string }> {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`)
      .pipe(
        map((response) => ({
          message: response.message || 'Servicio eliminado exitosamente',
        })),
      );
  }

  /**
   * Actualiza el status de un servicio (activar/inactivar)
   * La API devuelve solo { message: "..." } sin data
   *
   * Endpoint: PATCH /api/settings/services/{id}/status
   */
  updateStatus(
    id: string,
    status: 'ACTIVE' | 'INACTIVE',
  ): Observable<{ message: string }> {
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${id}/status`, { status })
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
        })),
      );
  }

  /**
   * Exporta la lista de servicios a PDF
   * La API genera y descarga el PDF directamente
   */
  exportPdf(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de servicios')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/pdf/services`, {
      params,
      responseType: 'blob',
    });
  }

  /**
   * Exporta la lista de servicios a Excel
   * La API genera y descarga el archivo Excel directamente
   */
  exportExcel(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de servicios')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', (options?.page ?? 0).toString())
      .set('size', (options?.size ?? 1000).toString());

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/services`,
      {
        params,
        responseType: 'blob',
      },
    );
  }
}
